import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getFileIcon } from "../../utils/fileIconUtils";
import { getLinkIcon } from "../../utils/businessCardUtils";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
} from "react-icons/hi";
import VCardGenerator from "./VCardGenerator";
import { LinkItem } from "./LinkManager";
import BusinessCardFooter from "./BusinessCardFooter";
import BusinessCardShareButton from "./BusinessCardShareButton";
import UserRegistrationSection from "./UserRegistrationSection";
import BookingLinkButton from "./BookingLinkButton";
import { BusinessCardBooking } from "../../types/BusinessCardTypes";
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessCardEmailLogin from "../pages/businessCardView/BusinessCardEmailLogin";
import ExpandableDescription from "./ExpandableDescription";
import LanguageSelector from "./LanguageSelector";
import SmartLink from "./SmartLink";
import PreviewSafeAnchor from "./PreviewSafeAnchor";
import PreviewSafeButton from "./PreviewSafeButton";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  enableUserRegistration?: boolean;
  showFooter?: boolean;
  booking?: BusinessCardBooking;
}

interface BusinessCardBoldDisplayProps {
  userData: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  businessCardData: BusinessCardData;
  companyLogo?: string;
  companyName?: string;
  companyDescription?: string;
  className?: string;
  isPreview?: boolean;
  processId?: string;
  enableUserRegistration?: boolean;
  entityIdentification: string;
  ownerIdentification: string;
  onEmailClick?: () => void;
}

const BusinessCardBoldDisplay: React.FC<BusinessCardBoldDisplayProps> = (
  props
) => {
  const {
    userData,
    businessCardData,
    companyLogo,
    companyName,
    companyDescription,
    className = "",
    isPreview = false,
    processId,
    enableUserRegistration = false,
    entityIdentification,
    ownerIdentification,
    onEmailClick,
  } = props;
  const { t } = useTranslation("clientView");
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);

  const handleFlipToEmail = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setShowEmailLogin(true);
      onEmailClick?.();
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const handleFileDownload = (cdnPath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = cdnPath;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const primaryLinks = businessCardData.links?.slice(0, 3) || [];
  const additionalLinks = businessCardData.links?.slice(3) || [];
  const hasFiles =
    businessCardData.uploadedFiles && businessCardData.uploadedFiles.length > 0;

  const headerContent = (companyName || companyLogo || companyDescription) && (
    <div className="border-b border-yellow-400">
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 relative">
        <div className="absolute top-2 end-2 z-[9999]">
          <LanguageSelector variant="compact" />
        </div>
        <div>
          {companyName && (
            <p
              className={`${
                companyName.length > 20 ? "text-xs" : "text-base"
              } font-black text-black uppercase tracking-wider text-start`}
            >
              <bdi>{companyName}</bdi>
            </p>
          )}
          {(companyLogo || companyDescription) && (
            <div className={`flex items-start gap-2 mt-1`}>
              {companyLogo && (
                <div className="w-10 h-10 flex-shrink-0">
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className="w-full h-full object-contain shadow-sm border bg-white rounded-lg"
                  />
                </div>
              )}
              {companyDescription && (
                <p className="text-xs text-black font-bold italic text-start">
                  <bdi>"{companyDescription}"</bdi>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const mainContent = showEmailLogin ? (
    <div className="flex flex-col gap-3 sm:gap-4">
      <BusinessCardEmailLogin
        isDark={true}
        entityIdentification={entityIdentification}
        ownerIdentification={ownerIdentification}
        onBackToCard={() => {
          if (isFlipping) return;
          setIsFlipping(true);
          setTimeout(() => setShowEmailLogin(false), 300);
          setTimeout(() => setIsFlipping(false), 600);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="pb-3 border-b border-yellow-400">
        <div className="flex items-center gap-4">
          <div className="relative">
            {userData.avatar ? (
              <img
                src={userData.avatar}
                alt={`${userData.first_name} ${userData.last_name}`}
                className="w-16 h-16 rounded-full object-cover border-4 border-yellow-400 shadow-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                {userData.first_name?.[0] || userData.last_name?.[0] ? (
                  <span className="text-lg font-black text-gray-500 uppercase">
                    {userData.first_name?.[0]}
                    {userData.last_name?.[0]}
                  </span>
                ) : (
                  <HiOutlineUser className="w-8 h-8 text-gray-400" />
                )}
              </div>
            )}
            <div className="absolute -inset-1 rounded-full border-2 border-orange-400"></div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-black text-yellow-400 uppercase tracking-wide break-words">
              <bdi>
                {userData.first_name} {userData.last_name}
              </bdi>
            </h1>
            <p className="text-sm font-bold text-orange-300 uppercase tracking-widest break-words text-start">
              <bdi>{businessCardData.jobTitle}</bdi>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <PreviewSafeAnchor
          href={`mailto:${userData.email}`}
          className="grid grid-cols-[auto_1fr] gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-black hover:bg-gray-900 text-white rounded-lg transition-colors duration-200 border-2 border-yellow-400 items-center"
        >
          <HiOutlineMail className="w-5 h-5 text-yellow-400" />
          <div className="min-w-0">
            <p
              dir="auto"
              className="text-xs text-yellow-300 font-medium text-start"
            >
              {t("key98")}
            </p>
            <p
              className="font-bold text-xs sm:text-sm uppercase tracking-wide truncate"
              title={userData.email}
            >
              <bdi>{userData.email}</bdi>
            </p>
          </div>
        </PreviewSafeAnchor>

        {userData.phone && (
          <PreviewSafeAnchor
            href={`tel:${userData.phone}`}
            className="flex items-center gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-colors duration-200 border-2 border-yellow-400"
          >
            <HiOutlinePhone className="w-5 h-5 text-yellow-400" />
            <p className="font-bold text-xs sm:text-sm uppercase tracking-wide">
              <bdi>{userData.phone}</bdi>
            </p>
          </PreviewSafeAnchor>
        )}
      </div>

      {businessCardData.description && (
        <div className="p-4 bg-black rounded-xl border-4 border-yellow-400 shadow-lg">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-4 h-4 bg-yellow-400 rounded-full"></div>
            <p className="text-sm font-black text-yellow-400 uppercase tracking-widest text-start">
              {t("key99")}
            </p>
            <div className="w-4 h-4 bg-orange-400 rounded-full"></div>
          </div>

          <ExpandableDescription
            content={businessCardData.description}
            maxLength={150}
            maxLines={5}
            expandText={t("key97") as string}
            collapseText={t("key107") as string}
            textClassName="text-sm text-white leading-relaxed font-bold uppercase tracking-wide"
            buttonClassName="mt-2 inline-flex items-center text-xs font-bold text-yellow-400 hover:text-orange-400 transition-colors duration-200 uppercase tracking-wide"
          />
        </div>
      )}

      {primaryLinks.length > 0 && (
        <div className="space-y-2 z-[99] flex flex-col">
          <div className="text-center">
            <p
              dir="auto"
              className="text-sm font-black text-black uppercase tracking-widest bg-yellow-400 inline-block px-4 py-1 rounded-full text-start"
            >
              {t("key100")}
            </p>
          </div>
          {primaryLinks.map((link, index) => {
            const boldGradients = [
              "from-red-600 to-red-700",
              "from-orange-600 to-orange-700",
              "from-yellow-600 to-yellow-700",
            ];
            const gradient = boldGradients[index % boldGradients.length];

            return (
              <SmartLink
                key={index}
                href={link.link}
                fallbackHref={link.fallbackLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 sm:gap-3 p-2 bg-gradient-to-r ${gradient} hover:scale-95 text-white rounded-lg transition-all duration-200 group border-2 border-yellow-400`}
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                  {getLinkIcon(link, "text-yellow-400")}
                </div>
                <p className="flex-1 text-sm font-bold text-white group-hover:text-yellow-200 uppercase tracking-wide w-full truncate text-start">
                  {link.label}
                </p>
                <div className="text-yellow-400 font-black">→</div>
              </SmartLink>
            );
          })}
        </div>
      )}

      {additionalLinks.length > 0 && (
        <div>
          {showAllLinks && (
            <div className="space-y-2 mb-2">
              {additionalLinks.map((link, index) => {
                const boldGradients = [
                  "from-purple-600 to-purple-700",
                  "from-pink-600 to-pink-700",
                  "from-indigo-600 to-indigo-700",
                ];
                const gradient = boldGradients[index % boldGradients.length];

                return (
                  <SmartLink
                    key={index + 3}
                    href={link.link}
                    fallbackHref={link.fallbackLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 sm:gap-3 p-2 bg-gradient-to-r ${gradient} hover:scale-95 text-white rounded-lg transition-all duration-200 group border-2 border-yellow-400`}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                      {getLinkIcon(link, "text-yellow-400")}
                    </div>
                    <p className="flex-1 text-sm font-bold text-white group-hover:text-yellow-200 uppercase tracking-wide truncate text-start">
                      {link.label}
                    </p>
                    <div className="text-yellow-400 font-black">→</div>
                  </SmartLink>
                );
              })}
            </div>
          )}
          <PreviewSafeButton
            onClick={() => setShowAllLinks(!showAllLinks)}
            className="flex items-center justify-center w-full p-2 text-sm text-black font-black bg-yellow-400 hover:bg-orange-400 rounded-lg transition-colors duration-200 uppercase tracking-wide"
            dir="auto"
          >
            <span className="me-1 text-start">
              {showAllLinks
                ? t("key101")
                : t("key102", { count: additionalLinks.length })}
            </span>
            {showAllLinks ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>
        </div>
      )}

      {hasFiles && (
        <div>
          <PreviewSafeButton
            onClick={() => setShowFiles(!showFiles)}
            className="flex items-center justify-between w-full p-2 text-sm text-black font-black bg-orange-400 hover:bg-red-400 rounded-lg transition-colors duration-200 uppercase tracking-wide"
            dir="auto"
          >
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4" />
              <span className="text-start">
                {t("businessCard.files", {
                  count: businessCardData.uploadedFiles?.length || 0,
                })}
              </span>
            </div>
            {showFiles ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>

          {showFiles && businessCardData.uploadedFiles && (
            <div className="mt-2 space-y-1">
              {businessCardData.uploadedFiles.map((file, index) => (
                <PreviewSafeButton
                  key={index}
                  onClick={() =>
                    handleFileDownload(file.cdnPath, file.fileName)
                  }
                  className="w-full flex items-center gap-2 p-2 bg-black hover:bg-gray-900 rounded-lg transition-colors duration-200 text-left group border border-yellow-400"
                  dir="auto"
                >
                  <span className="text-sm">{getFileIcon(file.fileName)}</span>
                  <p className="flex-1 text-sm font-bold text-yellow-400 group-hover:text-orange-400 uppercase truncate text-start">
                    {file.displayName}
                  </p>
                  <HiOutlineDownload className="w-4 h-4 text-yellow-400 group-hover:text-orange-400" />
                </PreviewSafeButton>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const showSaveContact = businessCardData.allowVCardDownload === true;
        const showShare = !!processId;
        if (!showSaveContact && !showShare) return null;

        return (
          <div
            className={`grid gap-2 ${
              showSaveContact && showShare ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {showSaveContact && (
              <VCardGenerator
                data={{
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  jobTitle: businessCardData.jobTitle,
                  email: userData.email,
                  phone: userData.phone,
                  description: businessCardData.description,
                  links: businessCardData.links,
                  avatar: userData.avatar,
                }}
                buttonColor="red"
              />
            )}
            {showShare && (
              <BusinessCardShareButton
                processId={processId}
                size="md"
                variant="secondary"
                logoUrl={companyLogo}
              />
            )}
          </div>
        );
      })()}

      <BookingLinkButton
        booking={businessCardData.booking}
        entityIdentification={entityIdentification}
        memberId={userData.user_identification}
      />

      {enableUserRegistration && processId && entityIdentification && (
        <UserRegistrationSection
          processId={processId}
          entityData={{
            entity_identification: entityIdentification,
            entity_name: companyName,
            entity_logo: companyLogo,
          }}
          theme="classic"
          size="sm"
          onEmailLogin={handleFlipToEmail}
        />
      )}
    </div>
  );

  const frontContent = (
    <div className="w-full">
      <div
        className="
        w-full min-h-full relative
        bg-gradient-to-br from-red-600 via-orange-600 to-yellow-600
        rounded-xl overflow-hidden
        border-4 border-yellow-400 shadow-lg
      "
      >
        {headerContent && (
          <div className="row-start-1 row-end-2">{headerContent}</div>
        )}

        <div className="row-start-2 row-end-3">
          <div className="px-1 sm:px-1 py-3 sm:py-4">{mainContent}</div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter
            showFooter={businessCardData.showFooter as boolean}
            theme="bold"
          />
        </div>
      </div>
    </div>
  );

  return (
    <BusinessCardContainer
      isPreview={isPreview}
      className={`w-full h-auto min-h-0 ${className}`}
      frontContent={frontContent}
      isFlipping={isFlipping}
      showEmailLogin={showEmailLogin}
    />
  );
};

export default BusinessCardBoldDisplay;
